import CloseIcon from 'assets/svg/app/close.svg'
import Button from 'components/Button'
import UnderlineTabs from 'components/Button/UnderlineTabs'
import { FlexDivCol, FlexDivRow, FlexDivRowCentered } from 'components/layout/flex'
import { type FC, memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import DepositInfo from 'sections/accounts/Deposits/DepositInfo'
import { DepositTab, DrawerTab } from 'sections/accounts/constants'
import { SectionHeading } from 'sections/shared/components/SectionHeading'
import { setOpenModal, setOpenModalWithParams } from 'state/app/reducer'
import { useAppDispatch } from 'state/hooks'
import styled from 'styled-components'
import { resetButtonCSS } from 'styles/common'

type DepositPanelProps = {
	onDismiss: () => void
}

const DepositPanel: FC<DepositPanelProps> = memo(({ onDismiss }) => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()

	const depositTabs = useMemo(
		() => [
			{
				name: DepositTab.CRYPTO,
				label: t('account.deposit-section.tabs.crypto-deposit'),
				active: true,
				onClick: () => {},
			},
			{
				name: DepositTab.FIAT,
				label: t('account.deposit-section.tabs.fiat'),
				active: false,
				disabled: true,
				onClick: () => {},
			},
		],
		[t]
	)

	return (
		<Container>
			<ContentContainer rowGap="15px">
				<FlexDivRowCentered>
					<SectionHeading title={t('account.deposit-section.title')} />
					<DismissButton onClick={onDismiss}>
						<CloseIcon width={12} height={12} />
					</DismissButton>
				</FlexDivRowCentered>
				<TabButtonsContainer>
					<UnderlineTabs options={depositTabs} noBorders={true} />
				</TabButtonsContainer>
				<DepositInfo />
			</ContentContainer>
			<Footer columnGap="10px">
				<Button
					size="small"
					fullWidth
					onClick={() => {
						dispatch(
							setOpenModalWithParams({
								type: 'manage_wallet',
								params: { tab: DrawerTab.WALLET },
							})
						)
					}}
				>
					{t('account.deposit-section.back-button')}
				</Button>
				<Button
					size="small"
					variant="solid-highlight"
					fullWidth
					onClick={() => dispatch(setOpenModal(null))}
				>
					{t('account.deposit-section.done-button')}
				</Button>
			</Footer>
		</Container>
	)
})

const Container = styled(FlexDivCol)`
	position: relative;
	height: 100%;
	min-height: 400px;
	overflow-y: scroll;
`

const ContentContainer = styled(FlexDivCol)`
	padding: 20px;
	margin-bottom: 60px;
`

const Footer = styled(FlexDivRow)`
	position: fixed;
	bottom: 0;
	width: 100%;
	border-top: 1px solid ${(props) => props.theme.colors.selectedTheme.newTheme.border.color};
	padding: 15px;
	background-color: ${(props) => props.theme.colors.selectedTheme.newTheme.containers.cards.background};
`

const DismissButton = styled.button`
	${resetButtonCSS};
	position: absolute;
	top: 25px;
	right: 20px;
	color: ${(props) => props.theme.colors.selectedTheme.gray};
	&:hover {
		color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	}
`

const TabButtonsContainer = styled.div`
	display: flex;
	min-height: 46px;
	gap: 15px;

	button {
		font-size: 13px;
	}

	> div > div {
		padding-left: 0px;
	}

	border-bottom: ${(props) => props.theme.colors.selectedTheme.border};
`

export default DepositPanel
